import { useEffect, useState, useCallback } from "react";
import { Box, Flex, Heading, Text, Button, Stack, HStack, Icon, SimpleGrid, Spinner, Input } from "@chakra-ui/react";
import { BarChart3, OctagonAlert, Timer, CheckCircle2, Inbox, X, ShieldAlert } from "lucide-react";
import {
  ResponsiveContainer, AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, LabelList,
} from "recharts";
import { supabase } from "@/lib/supabaseClient";
import { useAuth } from "@/context/AuthContext";
import { cachePut, cacheGet, safeQuery, safeAll } from "@/lib/offline";
import { colors } from "@/theme/colors";

const SEVERITY_COLORS = {
  Minor: "#7E9B76", Moderate: "#E0A63A", Serious: "#D9482B", Critical: "#A32D2D", "Not recorded": "#B8B2A5",
};
const RATING_COLORS = { Pass: "#5E7757", "Pass with notes": "#7E9B76", Fail: "#D9482B", "Not rated": "#B8B2A5" };
const PALETTE = ["#25A1B0", "#5E7757", "#E0A63A", "#D9482B", "#0E7C8B", "#7E9B76", "#A32D2D", "#B8B2A5"];

const Select = ({ value, onChange, children, testId }) => (
  <Box as="select" value={value} onChange={onChange} data-testid={testId}
    px="3" h="40px" borderRadius="8px" border="1px solid" borderColor={colors.border}
    bg={colors.white} fontSize="sm" color={colors.textPrimary} minW="150px">
    {children}
  </Box>
);

const StatCard = ({ icon, label, value, sub, accent }) => (
  <Box bg={colors.white} border="1px solid" borderColor={colors.border} borderRadius="14px" p="4"
    data-testid={`ana-stat-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
    <HStack gap="2" mb="2">
      <Flex align="center" justify="center" w="30px" h="30px" borderRadius="8px" bg={accent.bg}>
        <Icon as={icon} boxSize="4" color={accent.fg} />
      </Flex>
      <Text fontSize="xs" color={colors.textSecondary}>{label}</Text>
    </HStack>
    <Heading fontFamily="heading" fontWeight="700" fontSize="2xl" color={colors.textPrimary}>{value}</Heading>
    {sub && <Text fontSize="xs" color={colors.textSecondary} mt="0.5">{sub}</Text>}
  </Box>
);

const ChartCard = ({ title, empty, children, testId, span }) => (
  <Box bg={colors.white} border="1px solid" borderColor={colors.border} borderRadius="14px" p="5"
    gridColumn={{ base: "auto", lg: span ? `span ${span}` : "auto" }} data-testid={testId}>
    <Text fontWeight="700" fontSize="sm" color={colors.textPrimary} mb="3">{title}</Text>
    {empty ? (
      <Flex align="center" justify="center" h="220px" borderRadius="12px" border="1px dashed" borderColor={colors.border} bg={colors.page}>
        <Text fontSize="sm" color={colors.textSecondary}>No data yet for this view.</Text>
      </Flex>
    ) : children}
  </Box>
);

const tooltipStyle = {
  contentStyle: { borderRadius: 10, border: `1px solid ${colors.border}`, fontSize: 13 },
  cursor: { fill: "rgba(37,161,176,0.06)" },
};

const fmtDuration = (h) => {
  if (h === null || h === undefined) return "—";
  if (h >= 48) return `${(h / 24).toFixed(1)} days`;
  return `${h} h`;
};

export default function Analytics() {
  const { user, isSuperAdmin, activeCompanyId, companies, companyName } = useAuth();
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [data, setData] = useState(null);
  const [locOptions, setLocOptions] = useState([]);
  const [f, setF] = useState({ company: "", location: "", from: "", to: "" });

  const companyList = Object.values(companies || {});
  const scopeCompany = isSuperAdmin ? (f.company || null) : activeCompanyId;

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    const { data: d, error } = await safeQuery(supabase.rpc("analytics_data", {
      p_company: scopeCompany,
      p_from: f.from ? `${f.from}T00:00:00Z` : null,
      p_to: f.to ? `${f.to}T23:59:59Z` : null,
      p_location: f.location || null,
    }));
    const unfiltered = !f.company && !f.location && !f.from && !f.to;
    if (error) {
      // Offline: serve the last-known-good default view instead of an error.
      const snap = unfiltered ? cacheGet(user.id, "analytics") : null;
      if (snap) {
        setData(snap.data);
        setLocOptions(snap.locOptions || []);
      } else setErr(error.message);
    } else {
      setData(d);
      if (!f.location) setLocOptions((d?.locations || []).slice().sort());
      if (unfiltered) cachePut(user.id, "analytics", { data: d, locOptions: (d?.locations || []).slice().sort() });
    }
    setLoading(false);
  }, [scopeCompany, f.company, f.from, f.to, f.location, user.id]);
  useEffect(() => { load(); }, [load]);

  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value, ...(k === "company" ? { location: "" } : {}) }));
  const hasFilters = f.company || f.location || f.from || f.to;

  const compliance = data && data.inspections_rated > 0
    ? Math.round((data.inspections_passed / data.inspections_rated) * 100) : null;
  const factorNotRecorded = data ? Math.max(0, data.incidents_total - data.incidents_factor_recorded) : 0;
  const factorData = data ? [
    ...(data.incidents_by_factor || []),
    ...(factorNotRecorded > 0 ? [{ factor: "Not recorded", count: factorNotRecorded }] : []),
  ] : [];

  const scopeLabel = isSuperAdmin
    ? (f.company ? companyList.find((c) => c.id === f.company)?.name : "all companies")
    : companyName;

  return (
    <Box data-testid="analytics-page">
      <Stack gap="1" mb="5">
        <Heading fontFamily="heading" fontWeight="700" fontSize={{ base: "2xl", md: "3xl" }} color={colors.textPrimary}>Analytics</Heading>
        <Text color={colors.textSecondary} fontSize={{ base: "sm", md: "md" }}>
          Safety trends and insights for <Box as="span" fontWeight="600" color={colors.tealHover}>{scopeLabel}</Box>.
        </Text>
      </Stack>

      {/* Filters */}
      <Flex gap="2.5" flexWrap="wrap" align="center" mb="5" bg={colors.white} border="1px solid"
        borderColor={colors.border} borderRadius="14px" p="4" data-testid="ana-filters">
        {isSuperAdmin && (
          <Select value={f.company} onChange={set("company")} testId="ana-filter-company">
            <option value="">All companies</option>
            {companyList.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </Select>
        )}
        <Select value={f.location} onChange={set("location")} testId="ana-filter-location">
          <option value="">All locations</option>
          {locOptions.map((l) => <option key={l} value={l}>{l}</option>)}
        </Select>
        <HStack gap="1.5">
          <Input type="date" value={f.from} onChange={set("from")} data-testid="ana-filter-from"
            h="40px" w="150px" borderRadius="8px" borderColor={colors.border} bg={colors.white} fontSize="sm" />
          <Text fontSize="xs" color={colors.textSecondary}>to</Text>
          <Input type="date" value={f.to} onChange={set("to")} data-testid="ana-filter-to"
            h="40px" w="150px" borderRadius="8px" borderColor={colors.border} bg={colors.white} fontSize="sm" />
        </HStack>
        {hasFilters && (
          <Button size="sm" variant="ghost"
            onClick={() => setF({ company: "", location: "", from: "", to: "" })} data-testid="ana-clear-filters">
            <X size={14} /> Clear
          </Button>
        )}
      </Flex>

      {err && <Box mb="4" px="4" py="3" borderRadius="10px" bg={colors.criticalBg} color={colors.criticalText} fontSize="sm" data-testid="ana-error">{err}</Box>}

      {loading || !data ? (
        <Flex minH="30vh" align="center" justify="center"><Spinner color={colors.teal} borderWidth="3px" /></Flex>
      ) : (
        <>
          <SimpleGrid columns={{ base: 2, md: 3, xl: 5 }} gap="4" mb="5">
            <StatCard icon={Inbox} label="Total submissions" value={data.total}
              accent={{ bg: colors.tealTint, fg: colors.teal }} />
            <StatCard icon={CheckCircle2} label="Open vs resolved" value={`${data.open} / ${data.resolved}`}
              sub="open incl. logged / resolved" accent={{ bg: "#EAF0E7", fg: colors.sageDeep }} />
            <StatCard icon={Timer} label="Avg time to resolve" value={fmtDuration(data.avg_resolution_hours)}
              accent={{ bg: "#FBEEDA", fg: "#8A5106" }} />
            <StatCard icon={OctagonAlert} label="STOP WORK events" value={data.stop_work}
              sub="risk still Critical after controls" accent={{ bg: colors.poppyTint, fg: colors.poppy }} />
            <StatCard icon={BarChart3} label="Inspection compliance" value={compliance === null ? "—" : `${compliance}%`}
              sub={data.inspections_rated ? `${data.inspections_passed}/${data.inspections_rated} passed` : "no rated inspections"}
              accent={{ bg: colors.tealTint, fg: colors.tealHover }} />
          </SimpleGrid>

          <SimpleGrid columns={{ base: 1, lg: 2 }} gap="5">
            <ChartCard title="Submissions over time" empty={!data.over_time.length} testId="chart-over-time" span={2}>
              <ResponsiveContainer width="100%" height={230}>
                <AreaChart data={data.over_time} margin={{ top: 6, right: 12, left: -18, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={colors.border} />
                  <XAxis dataKey="day" tick={{ fontSize: 11, fill: colors.textSecondary }}
                    tickFormatter={(d) => new Date(`${d}T00:00:00`).toLocaleDateString([], { month: "short", day: "numeric" })} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: colors.textSecondary }} />
                  <Tooltip {...tooltipStyle} />
                  <Area type="monotone" dataKey="count" name="Submissions" stroke={colors.teal} strokeWidth={2}
                    fill={colors.tealTint} dot={{ r: 3, fill: colors.teal }} />
                </AreaChart>
              </ResponsiveContainer>
            </ChartCard>

            <ChartCard title="Breakdown by form type" empty={!data.by_type.length} testId="chart-by-type">
              <ResponsiveContainer width="100%" height={230}>
                <BarChart data={data.by_type} margin={{ top: 14, right: 12, left: -18, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={colors.border} />
                  <XAxis dataKey="type" tick={{ fontSize: 11, fill: colors.textSecondary }} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: colors.textSecondary }} />
                  <Tooltip {...tooltipStyle} />
                  <Bar dataKey="count" name="Submissions" radius={[6, 6, 0, 0]}>
                    <LabelList dataKey="count" position="top" style={{ fontSize: 11, fill: colors.textSecondary }} />
                    {data.by_type.map((d, i) => <Cell key={d.type} fill={PALETTE[i % PALETTE.length]} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>

            <ChartCard title="Incident reports per location" empty={!data.incidents_by_location.length} testId="chart-incidents-location">
              <ResponsiveContainer width="100%" height={230}>
                <BarChart data={data.incidents_by_location} layout="vertical" margin={{ top: 0, right: 24, left: 30, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={colors.border} horizontal={false} />
                  <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11, fill: colors.textSecondary }} />
                  <YAxis type="category" dataKey="location" width={110} tick={{ fontSize: 11, fill: colors.textSecondary }} />
                  <Tooltip {...tooltipStyle} />
                  <Bar dataKey="count" name="Incidents" fill={colors.teal} radius={[0, 6, 6, 0]} barSize={18}>
                    <LabelList dataKey="count" position="right" style={{ fontSize: 11, fill: colors.textSecondary }} />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>

            <ChartCard title="Incidents by severity" empty={!data.incidents_by_severity.length} testId="chart-incidents-severity">
              <Flex align="center" gap="4" flexWrap="wrap">
                <ResponsiveContainer width="55%" height={220}>
                  <PieChart>
                    <Pie data={data.incidents_by_severity} dataKey="count" nameKey="severity"
                      innerRadius={52} outerRadius={85} paddingAngle={2}>
                      {data.incidents_by_severity.map((s, i) => (
                        <Cell key={s.severity} fill={SEVERITY_COLORS[s.severity] || PALETTE[i % PALETTE.length]} />
                      ))}
                    </Pie>
                    <Tooltip {...tooltipStyle} />
                  </PieChart>
                </ResponsiveContainer>
                <Stack gap="1.5" flex="1" minW="120px">
                  {data.incidents_by_severity.map((s, i) => (
                    <HStack key={s.severity} gap="2">
                      <Box w="10px" h="10px" borderRadius="3px" bg={SEVERITY_COLORS[s.severity] || PALETTE[i % PALETTE.length]} />
                      <Text fontSize="xs" color={colors.textPrimary}>{s.severity}</Text>
                      <Text fontSize="xs" color={colors.textSecondary} ml="auto">{s.count}</Text>
                    </HStack>
                  ))}
                </Stack>
              </Flex>
            </ChartCard>

            <ChartCard title="Incidents by contributing factor / root cause" empty={!factorData.length} testId="chart-incidents-factor">
              <ResponsiveContainer width="100%" height={Math.max(200, factorData.length * 34 + 30)}>
                <BarChart data={factorData} layout="vertical" margin={{ top: 0, right: 24, left: 60, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={colors.border} horizontal={false} />
                  <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11, fill: colors.textSecondary }} />
                  <YAxis type="category" dataKey="factor" width={150} tick={{ fontSize: 11, fill: colors.textSecondary }} />
                  <Tooltip {...tooltipStyle} />
                  <Bar dataKey="count" name="Incidents" fill={colors.sageDeep} radius={[0, 6, 6, 0]} barSize={16}>
                    <LabelList dataKey="count" position="right" style={{ fontSize: 11, fill: colors.textSecondary }} />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>

            <ChartCard title="Inspection results (Pass vs Fail)" empty={!data.inspection_ratings.length} testId="chart-inspection-ratings" span={2}>
              <Flex align="center" gap="6" flexWrap="wrap">
                <ResponsiveContainer width="45%" height={220}>
                  <PieChart>
                    <Pie data={data.inspection_ratings} dataKey="count" nameKey="rating"
                      innerRadius={52} outerRadius={85} paddingAngle={2}>
                      {data.inspection_ratings.map((r, i) => (
                        <Cell key={r.rating} fill={RATING_COLORS[r.rating] || PALETTE[i % PALETTE.length]} />
                      ))}
                    </Pie>
                    <Tooltip {...tooltipStyle} />
                  </PieChart>
                </ResponsiveContainer>
                <Stack gap="2" flex="1" minW="180px">
                  {data.inspection_ratings.map((r, i) => (
                    <HStack key={r.rating} gap="2">
                      <Box w="10px" h="10px" borderRadius="3px" bg={RATING_COLORS[r.rating] || PALETTE[i % PALETTE.length]} />
                      <Text fontSize="sm" color={colors.textPrimary}>{r.rating}</Text>
                      <Text fontSize="sm" color={colors.textSecondary} ml="auto">{r.count}</Text>
                    </HStack>
                  ))}
                  {compliance !== null && (
                    <HStack mt="2" px="3" py="2" borderRadius="10px" bg={colors.tealTint}>
                      <Icon as={ShieldAlert} boxSize="4" color={colors.tealHover} />
                      <Text fontSize="sm" color={colors.tealHover} fontWeight="600">
                        Compliance rate: {compliance}% of rated inspections passed
                      </Text>
                    </HStack>
                  )}
                </Stack>
              </Flex>
            </ChartCard>
          </SimpleGrid>
        </>
      )}
    </Box>
  );
}
